import { useState, useCallback, useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { omit } from 'lodash';

import type { IObject } from '@/types';

export interface EvaluationFilters extends IObject {
  division: string;
  applications: string[];
  environments: string[];
  ipmOnly: boolean;
  evaluatedOnly: boolean;
  activeOnly: boolean;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  limit: number;
  page: number;
}

interface StoredFilters {
  version: number;
  filters: Partial<EvaluationFilters>;
}

interface UseEvaluationFiltersReturn {
  filters: EvaluationFilters;
  setFilters: (
    filters:
      | EvaluationFilters
      | ((prev: EvaluationFilters) => EvaluationFilters),
  ) => void;
  updateFilters: (updates: Partial<EvaluationFilters>) => void;
  setPage: (page: number) => void;
  resetFilters: () => void;
}

const DEFAULT_FILTERS: EvaluationFilters = {
  division: 'ALL',
  applications: [],
  environments: [],
  ipmOnly: false,
  evaluatedOnly: true,
  activeOnly: true,
  sortBy: 'generatedAt',
  sortOrder: 'desc',
  limit: 30,
  page: 1,
};

const FILTERS_KEY = 'evaluationListFilters';
const FILTERS_VERSION = 0;

// Fields that should NOT be persisted in localStorage
const NON_PERSISTED_FIELDS: (keyof EvaluationFilters)[] = ['page'];

export const useEvaluationFilters = (): UseEvaluationFiltersReturn => {
  // Get persisted filters from localStorage
  const [persistedData, setPersistedData] = useLocalStorage<StoredFilters>(
    FILTERS_KEY,
    {
      version: FILTERS_VERSION,
      filters: omit(DEFAULT_FILTERS, NON_PERSISTED_FIELDS),
    },
    {
      serializer: (values) => JSON.stringify(values),
      deserializer: (values) => JSON.parse(values),
    },
  );

  // Check if stored version is lower than current version
  const shouldUseDefaults = persistedData.version < FILTERS_VERSION;

  // Keep all state together in local state
  const [localFilters, setLocalFilters] = useState<EvaluationFilters>(() => {
    if (shouldUseDefaults) {
      return DEFAULT_FILTERS;
    }
    return {
      ...DEFAULT_FILTERS,
      ...persistedData.filters,
    };
  });

  // Combine persisted and local state
  const filters: EvaluationFilters = useMemo(
    () => ({
      ...DEFAULT_FILTERS,
      ...(shouldUseDefaults ? {} : persistedData.filters),
      ...localFilters,
    }),
    [persistedData.filters, localFilters, shouldUseDefaults],
  );

  const setFilters = useCallback(
    (
      newFilters:
        | EvaluationFilters
        | ((prev: EvaluationFilters) => EvaluationFilters),
    ) => {
      const updatedFilters =
        typeof newFilters === 'function' ? newFilters(filters) : newFilters;

      // Update local state
      setLocalFilters(updatedFilters);

      // Update persisted state (excluding non-persisted fields)
      const fieldsToPersist = omit(updatedFilters, NON_PERSISTED_FIELDS);
      setPersistedData({
        version: FILTERS_VERSION,
        filters: fieldsToPersist,
      });
    },
    [filters, setPersistedData],
  );

  const resetFilters = useCallback(() => {
    setLocalFilters(DEFAULT_FILTERS);
    setPersistedData({
      version: FILTERS_VERSION,
      filters: omit(DEFAULT_FILTERS, NON_PERSISTED_FIELDS),
    });
  }, [setPersistedData]);

  const updateFilters = useCallback(
    (updates: Partial<EvaluationFilters>) => {
      setFilters((prev) => ({
        ...prev,
        ...updates,
        page: 1,
      }));
    },
    [setFilters],
  );

  const setPage = useCallback(
    (page: number) => {
      setFilters((prev) => ({
        ...prev,
        page,
      }));
    },
    [setFilters],
  );

  return {
    filters,
    setFilters,
    updateFilters,
    setPage,
    resetFilters,
  };
};
